import { useEffect, useMemo, useRef, useState } from 'react';
import styles from '../Login/Login.module.css';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import clsx from 'clsx';
import {
  getUserByToken,
  linkDescopeToTP,
  register,
  validateReferralCode,
} from '../../auth/_requests';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/Auth';
import { motion } from 'framer-motion';
import logoUrl from '../../assets/helpers/navbar/tenantpay-white.png';
import OAuth from '../../helpers/OAuth/OAuth';
import { useDescope } from '@descope/react-sdk';
import { showToast } from '../../helpers/utils/showToast';
import eyeOpen from '../../assets/pages/login/eye.svg';
import eyeOff from '../../assets/pages/login/eyeClosed.svg';
import { InlineAlert } from '../../helpers/UI/InlineAlert/InlineAlert';
import { useModal } from '../../helpers/hooks/modalContext';
import {
  clearSignupTermsAccepted,
  markSignupTermsAccepted,
  markStartPayRentSetup,
  saveSignupTermsConsent,
} from '../../helpers/utils/signupConsent';

import { logThirdPartyError } from '../../helpers/utils/safeSentryError';
import { setAnalyticsUser, trackSignUp, trackSignUpStart } from '../../helpers/utils/analytics';
import { AuthBrandedLoader } from '../Login/AuthBrandedLoader';
import { AuthPageShell } from '../Login/AuthPageShell';
import {
  PASSWORD_RULES,
  getPasswordRuleStates,
} from '../../helpers/utils/passwordRules';

const initialValues = {
  email: '',
  password: '',
  utm_source: '',
  utm_medium: '',
  utm_campaign: '',
  utm_term: '',
  utm_content: '',
  acquisitionSource: '',
};

const PLUS_ALIAS_EXEMPT_DOMAINS = ['theninjastudio.com', 'tenantpay.com'];

const registrationSchema =Yup.object().shape({
  email: Yup.string()
    .email('Invalid email format')
    .matches(/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/, 'Invalid email format')
    .test('no-plus-alias', 'Invalid email format', (value) => {
      if (!value || !value.includes('+')) return true;
      const domain = value.split('@')[1]?.toLowerCase();
      return PLUS_ALIAS_EXEMPT_DOMAINS.includes(domain);
    })
    .min(3, 'Minimum 3 characters')
    .max(50, 'Maximum 50 characters')
    .required('Email is required'),
  password: Yup.string()
    .min(8, 'Minimum 8 characters')
    .max(15, 'Maximum 15 characters')
    .matches(/[a-z]/, 'Must contain at least one lowercase letter')
    .matches(/[A-Z]/, 'Must contain at least one uppercase letter')
    .matches(/[0-9]/, 'Must contain at least one number')
    .matches(/[\W_]/, 'Must contain at least one special character')
    .matches(/^\S*$/, 'Password cannot contain spaces')
    .required('Password is required'),
  acquisitionSource: Yup.string()
    .trim()
    .max(191, 'Maximum 191 characters')
    .required('How you heard about us is required'),
});

export function SignUp() {
  const [loading, setLoading] = useState(false);
  const [loadingCode, setLoadingCode] = useState(false);
  const { referralCode } = useParams();
  const [searchParams] = useSearchParams();
  const ref = searchParams.get('ref');
  const { saveAuth, setCurrentUser } = useAuth();
  const { setModal } = useModal();
  const [showPassword, setShowPassword] = useState(false);
  const [serverError, setServerError] = useState<string>('');
  const signUpStartTrackedRef = useRef(false);

  const descope = useDescope();

  const navigate = useNavigate();
  const formik = useFormik({
    initialValues,
    validationSchema: registrationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setLoading(true);
      if (!signUpStartTrackedRef.current) {
        signUpStartTrackedRef.current = true;
        trackSignUpStart('email');
      }
      try {
        const { data: auth } = await register(
          values.email,
          values.password,
          referralCode || '',
          values.acquisitionSource.trim()
        );
        const signUpResponse = await descope.password.signUp(values.email, values.password);
        if (!signUpResponse.ok) {
          const error = new Error(signUpResponse.error?.errorDescription || 'Sign up failed');
          logThirdPartyError(error, {
            method: 'descope.password.signUp',
            email: values.email,
            errorDescription: signUpResponse.error?.errorDescription,
            errorCode: signUpResponse.error?.errorCode,
          });
          throw error;
        }

        await linkDescopeToTP(auth.data.token);
        saveAuth(auth);
        const { data: user } = await getUserByToken(auth.data.token);
        setCurrentUser(user);
        localStorage.setItem('isSignUp', 'true');

        setAnalyticsUser(user.user?.userId, undefined, auth.data.tenantId);
        trackSignUp('email');

        // Best-effort: persist consent server-side. Pay setup is always queued because
        // the user already agreed on this form — dashboard must not show the T&C modal.
        await saveSignupTermsConsent(auth.data.token);
        clearSignupTermsAccepted();
        markStartPayRentSetup();

        navigate('/verify-email');
      } catch (error: unknown) {
        console.error(error);
        // Log Descope errors to Sentry
        if (error instanceof Error && error.message !== 'email already registered') {
          logThirdPartyError(error, {
            method: 'descope.password.signUp',
            email: values.email,
          });
        }
        saveAuth(undefined);
        if (error instanceof Error) {
          if (error.message === 'email already registered') {
            setServerError('This email is linked to an existing account.');
          } else if (error.message === 'invalid_email') {
            setServerError('Invalid email address.');
          } else if (error.message === 'email_domain_blocked') {
            setServerError('This email domain is not allowed. Please use a different email address.');
          } else if (error.message === 'phone_number_blocked') {
            setServerError(
              'This phone number has been blocked due to suspicious activity. Please contact support@tenantpay.com for assistance.'
            );
          } else {
            setServerError('The registration details are incorrect.');
          }
        } else {
          setServerError('Something went wrong. Please try again later.');
        }
        setSubmitting(false);
      } finally {
        setLoading(false);
      }
    },
  });

  const isSubmitDisabled = formik.isSubmitting || !formik.isValid || !formik.dirty;

  const passwordRuleStates = useMemo(
    () => getPasswordRuleStates(formik.values.password),
    [formik.values.password]
  );
  const showPasswordRules = formik.values.password.length > 0;
  useEffect(() => {
    const validateReferralCodeFunction = async () => {
      setLoadingCode(true);
      try {
        if (referralCode) {
          const response = await validateReferralCode(referralCode);
          if (!response.result) {
            showToast({
              message: `The referral code provided is invalid. You can still sign up without a referral code.`,
              type: 'info',
            });
          } else {
            showToast({
              message:
                'Referral code applied successfully. You can proceed with your registration!',
              type: 'success',
            });
          }
        }
      } catch (error) {
        console.error('Error validating the referral code for the user');
      } finally {
        setLoadingCode(false);
      }
    };
    validateReferralCodeFunction();
  }, []);
  if (loadingCode) {
    return <AuthBrandedLoader message='Loading…' />;
  }
  return (
    <>
      <AuthPageShell fillViewportHeight variant='center'>
        <div className={`${styles.loginCardShell} max-w-[560px]`}>
          <motion.main className={`${styles.main} ${styles.mainWide}`} initial={false}>
            <div className={styles.logoWrap}>
              <img src={logoUrl} className={styles.logo} alt='logo' decoding='async' />
            </div>

            {serverError && (
              <InlineAlert
                type='error'
                message={serverError}
                dismissible
                onDismiss={() => setServerError('')}
              />
            )}
            <div>
              <div className={styles.formTitle}>Create your account.</div>
            </div>

            <div
              className={styles.oAuthOuter}
              onClick={(e) => {
                e.preventDefault();
                markSignupTermsAccepted();
              }}
            >
              <OAuth setLoading={setLoading} />
            </div>
            <div className={styles.authDivider}>
              <div className={styles.authDividerLine} />
              <span className={styles.authDividerText}>or continue with email</span>
              <div className={styles.authDividerLine} />
            </div>

            <form
              className={styles.form}
              onSubmit={formik.handleSubmit}
              noValidate
              id='kt_login_signup_form'
            >
              <div className={styles.formUpper}>
                <div className={styles.formBoxes}>
                  <div className={styles.formBoxesInner}>
                    <div className={'inputWrapper'} title='Enter your email to register'>
                      <div className={'label'}>Email</div>

                      <div
                        className={clsx(
                          formik.touched.email && formik.errors.email ? styles.isInvalid : '',
                          formik.touched.email && !formik.errors.email ? styles.valid : '',
                          'inputField'
                        )}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            formik.handleSubmit();
                          }
                        }}
                      >
                        <input
                          {...formik.getFieldProps('email')}
                          onChange={(event) => {
                            setServerError('');
                            formik.handleChange(event);
                          }}
                          placeholder='you@email.com'
                          type='email'
                          name='email'
                          autoComplete='email'
                        />
                      </div>
                      {formik.touched.email && formik.errors.email && (
                        <div className={styles.emailError}>
                          <span>{formik.errors.email}</span>
                        </div>
                      )}
                    </div>
                    <div
                      className={'inputWrapper'}
                      title='Password must be 8-15 characters long, including at least one uppercase letter, one lowercase letter, one number, one special character, and no spaces.'
                    >
                      <div className={'label'}>Password</div>
                      <div
                        className={clsx(
                          formik.touched.password && formik.errors.password ? styles.isInvalid : '',
                          formik.touched.password && !formik.errors.password ? styles.valid : '',
                          'inputField'
                        )}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                          }
                        }}
                      >
                        <input
                          type={showPassword ? 'text' : 'password'}
                          placeholder={'••••••••'}
                          value={formik.values.password}
                          onChange={(e) => {
                            formik.setFieldValue('password', e.target.value);
                          }}
                          autoComplete='new-password'
                          name='password'
                          onBlur={formik.handleBlur('password')}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              formik.handleSubmit();
                            }
                          }}
                        />

                        <button
                          type='button'
                          onClick={(event) => {
                            event.preventDefault();
                            setShowPassword(!showPassword);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                            }
                          }}
                        >
                          <img
                            src={showPassword ? eyeOpen : eyeOff}
                            alt='eye-img'
                            className={styles.eyeImg}
                          />
                        </button>
                      </div>
                      {showPasswordRules && (
                        <div className={styles.passwordRulesChecklist} aria-live='polite'>
                          {PASSWORD_RULES.map((rule) => {
                            const isMet = passwordRuleStates[rule.id];
                            return (
                              <div key={rule.id} className={styles.passwordRuleItem}>
                                <span
                                  className={clsx(
                                    styles.passwordRuleIcon,
                                    isMet && styles.passwordRuleIconMet
                                  )}
                                  aria-hidden
                                >
                                  {isMet && (
                                    <svg viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'>
                                      <path
                                        d='M2.5 6.2L4.8 8.5L9.5 3.5'
                                        stroke='currentColor'
                                        strokeWidth='2'
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                      />
                                    </svg>
                                  )}
                                </span>
                                <span
                                  className={clsx(
                                    styles.passwordRuleLabel,
                                    isMet && styles.passwordRuleLabelMet
                                  )}
                                >
                                  {rule.label}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {formik.touched.password &&
                        formik.errors.password &&
                        (!showPasswordRules ||
                          formik.values.password.length > 15 ||
                          /\s/.test(formik.values.password)) && (
                          <div className={styles.emailError}>
                            <div className='fv-help-block'>
                              <span>{formik.errors.password}</span>
                            </div>
                          </div>
                        )}
                    </div>
                    <div className={'inputWrapper'} title='How did you hear about us?'>
                      <div className={'label'}>How did you hear about us?</div>
                      <div
                        className={clsx(
                          formik.touched.acquisitionSource && formik.errors.acquisitionSource
                            ? styles.isInvalid
                            : '',
                          formik.touched.acquisitionSource &&
                            !formik.errors.acquisitionSource &&
                            formik.values.acquisitionSource
                            ? styles.valid
                            : '',
                          'inputField'
                        )}
                      >
                        <input
                          {...formik.getFieldProps('acquisitionSource')}
                          placeholder='Google, a friend, my landlord…'
                          type='text'
                          name='acquisitionSource'
                          autoComplete='off'
                          maxLength={191}
                          required
                          aria-required='true'
                        />
                      </div>
                      {formik.touched.acquisitionSource && formik.errors.acquisitionSource && (
                        <div className={styles.emailError}>
                          <span>{formik.errors.acquisitionSource}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={styles.termsConsentOuter}>
                    <p className={styles.termsConsentLabel}>
                      By clicking signup, you agree to{' '}
                      <a
                        href='#terms-and-conditions'
                        className={styles.termsConsentLink}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setModal('terms-services');
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        terms of use
                      </a>{' '}
                      and{' '}
                      <a
                        href='#privacy-policy'
                        className={styles.termsConsentLink}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setModal('privacy-policy');
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        privacy policy
                      </a>
                    </p>
                  </div>

                  <button
                    type='submit'
                    id='kt_signup_submit'
                    disabled={isSubmitDisabled}
                    className={`${styles.submitBtn} ${isSubmitDisabled && styles.disableBtn}`}
                  >
                    {!loading && <span className='indicator-label'>Sign up</span>}
                    {loading && (
                      <span className={styles.indicatorProgress} style={{ display: 'block' }}>
                        Please wait...
                        <span className={styles.spinner}></span>
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </form>

            <div className={styles.signInBox}>
              <span>{ref === 'website' ? 'Are you a landlord?' : 'Already a member?'}</span>{' '}
              <button
                type='button'
                onClick={(e) => {
                  e.preventDefault();
                  if (ref === 'website') {
                    window.open('https://manage.tenantpay.com/sign-up', '_blank');
                  } else {
                    navigate('/login');
                  }
                }}
                className={styles.forgotPassword}
              >
                {ref === 'website' ? 'Click here' : 'Sign in'}
              </button>
            </div>
          </motion.main>
        </div>
      </AuthPageShell>
      {loading && <AuthBrandedLoader message='Creating your account…' />}
    </>
  );
}
